import {
  getEntryChildId,
  getEntryId,
  getMedicationChangeAnchorTime,
  getMedicationChangeType,
  getObservation,
  isMedicationChangeAnchor,
  OBSERVATION_CATEGORIES,
  sortByDateThenId,
} from './medicationTimelinePatternObservationUtils';
import {
  buildEvidenceEntries,
  buildPatternSummary,
  buildRuleMatched,
  getSummaryForCategory,
  getWindowDaysForCategory,
  hasMeaningfulChange,
} from './medicationTimelinePatternComparisonUtils';

const getPatternEvidenceIds = (pattern = {}) => [
  ...(Array.isArray(pattern?.evidence?.before) ? pattern.evidence.before : []),
  ...(Array.isArray(pattern?.evidence?.after) ? pattern.evidence.after : []),
].map((item) => item?.entryId).filter(Boolean);

const getPatternEvidenceTimes = (pattern = {}) => [
  ...(Array.isArray(pattern?.evidence?.before) ? pattern.evidence.before : []),
  ...(Array.isArray(pattern?.evidence?.after) ? pattern.evidence.after : []),
].map((item) => new Date(item?.occurredAt)).filter((date) => !Number.isNaN(date.getTime()));

const getPatternEvidenceCenterTime = (pattern = {}) => {
  const times = getPatternEvidenceTimes(pattern).map((date) => date.getTime()).sort((a, b) => a - b);
  if (!times.length) return null;
  return new Date(times[Math.floor(times.length / 2)]);
};

const getPatternAnchorDistance = (pattern = {}) => {
  const anchorTime = pattern?.__anchorTime;
  const centerTime = getPatternEvidenceCenterTime(pattern);
  if (!anchorTime || !centerTime) return Number.POSITIVE_INFINITY;
  return Math.abs(anchorTime.getTime() - centerTime.getTime());
};

const getPatternEvidenceKey = (pattern = {}) => getPatternEvidenceIds(pattern).slice().sort().join('|');

const getPatternGroupKey = (pattern = {}) => [
  pattern?.childId || '',
  pattern?.medicationChange?.medicationId || '',
  pattern?.patternCategory || '',
].join('|');

const getPatternOverlapRatio = (a = [], b = []) => {
  const setA = new Set(a);
  const setB = new Set(b);
  let shared = 0;
  setA.forEach((value) => {
    if (setB.has(value)) shared += 1;
  });

  const smallerSize = Math.min(setA.size, setB.size);
  if (!smallerSize) return 0;
  return shared / smallerSize;
};

const dedupeMedicationChangeTimelinePatterns = (patterns = []) => {
  const groups = new Map();

  patterns.forEach((pattern) => {
    const groupKey = getPatternGroupKey(pattern);
    const group = groups.get(groupKey) || [];
    group.push(pattern);
    groups.set(groupKey, group);
  });

  const deduped = [];

  groups.forEach((groupPatterns) => {
    const ordered = groupPatterns
      .slice()
      .sort((a, b) => {
        const aTime = a?.__anchorTime?.getTime?.() || 0;
        const bTime = b?.__anchorTime?.getTime?.() || 0;
        return aTime - bTime || getPatternAnchorDistance(a) - getPatternAnchorDistance(b) || getEntryId(a).localeCompare(getEntryId(b));
      });

    const kept = [];

    ordered.forEach((candidate) => {
      const candidateIds = getPatternEvidenceIds(candidate);
      const candidateKey = getPatternEvidenceKey(candidate);
      const overlappingIndex = kept.findIndex((existing) => {
        const existingIds = getPatternEvidenceIds(existing);
        const existingKey = getPatternEvidenceKey(existing);
        return existingKey === candidateKey || getPatternOverlapRatio(existingIds, candidateIds) >= 0.75;
      });

      if (overlappingIndex < 0) {
        kept.push(candidate);
        return;
      }

      const existing = kept[overlappingIndex];
      const existingDistance = getPatternAnchorDistance(existing);
      const candidateDistance = getPatternAnchorDistance(candidate);
      if (candidateDistance < existingDistance || (candidateDistance === existingDistance && getEntryId(candidate).localeCompare(getEntryId(existing)) < 0)) {
        kept[overlappingIndex] = candidate;
      }
    });

    deduped.push(...kept);
  });

  return deduped;
};

const buildPatternForAnchor = ({ anchor, category, observations }) => {
  const effectiveAt = getMedicationChangeAnchorTime(anchor);
  if (!effectiveAt) return null;

  const windowDays = getWindowDaysForCategory(category);
  const windowMs = windowDays * 24 * 60 * 60 * 1000;

  const before = observations
    .filter((observation) => observation.occurredAt < effectiveAt)
    .filter((observation) => (effectiveAt.getTime() - observation.occurredAt.getTime()) <= windowMs)
    .sort(sortByDateThenId);
  const after = observations
    .filter((observation) => observation.occurredAt >= effectiveAt)
    .filter((observation) => (observation.occurredAt.getTime() - effectiveAt.getTime()) <= windowMs)
    .sort(sortByDateThenId);

  if (before.length < 2 || after.length < 2) return null;

  const beforeSummary = getSummaryForCategory(category, before);
  const afterSummary = getSummaryForCategory(category, after);
  if (!hasMeaningfulChange(category, beforeSummary, afterSummary)) return null;

  return {
    id: `medication-pattern-${getEntryId(anchor)}-${category}`,
    childId: getEntryChildId(anchor),
    medicationChange: {
      entryId: getEntryId(anchor),
      medicationId: anchor.medicationId,
      medicationChangeType: getMedicationChangeType(anchor),
      effectiveAt: effectiveAt.toISOString(),
    },
    __anchorTime: effectiveAt,
    patternCategory: category,
    windowDays: { before: windowDays, after: windowDays },
    status: 'active',
    summary: buildPatternSummary(category),
    comparison: {
      before: beforeSummary,
      after: afterSummary,
      ruleMatched: buildRuleMatched(category),
    },
    evidence: {
      before: buildEvidenceEntries(before, category),
      after: buildEvidenceEntries(after, category),
    },
    visibility: {
      showEvidenceIds: true,
      showEvidencePreview: true,
      showCausationWarning: true,
    },
  };
};

export const selectMedicationChangeTimelinePatterns = (entries = []) => {
  const normalizedEntries = (Array.isArray(entries) ? entries : [])
    .map((entry) => (entry && typeof entry === 'object' ? entry : null))
    .filter(Boolean);

  const anchors = normalizedEntries
    .filter(isMedicationChangeAnchor)
    .sort((a, b) => {
      const delta = getMedicationChangeAnchorTime(a).getTime() - getMedicationChangeAnchorTime(b).getTime();
      if (delta !== 0) return delta;
      return getEntryId(a).localeCompare(getEntryId(b));
    });

  const patterns = [];

  anchors.forEach((anchor) => {
    const anchorChildId = getEntryChildId(anchor);

    OBSERVATION_CATEGORIES.forEach((category) => {
      const observations = normalizedEntries
        .map((entry) => ({ entry, observation: getObservation(entry) }))
        .filter(({ observation }) => observation && observation.kind === category && observation.id !== getEntryId(anchor))
        .filter(({ observation }) => !anchorChildId || observation.childId === anchorChildId)
        .map(({ observation }) => observation);

      const pattern = buildPatternForAnchor({ anchor, category, observations });
      if (pattern) patterns.push(pattern);
    });
  });

  return dedupeMedicationChangeTimelinePatterns(patterns).map(({ __anchorTime, ...pattern }) => pattern);
};

export default selectMedicationChangeTimelinePatterns;
